import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  Typography,
} from "@mui/material";
import { DriveFileMoveOutlined } from "@mui/icons-material";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import FormFooter, {
  FOOTER_BUTTON_SX,
} from "components/userManagement/form/FormFooter";
import FormHeader from "components/userManagement/form/FormHeader";
import { pushNotification } from "features/notificationSlice";
import {
  type SecureFile,
  useCreateSecureCollectionMutation,
  useGetSecureMoveCheckQuery,
  useGetSecureProjectCollectionsQuery,
  useGetSecureProjectsQuery,
  useMoveSecureFileMutation,
} from "features/secureUploads/secureUploads";
import { useViewAs } from "hooks/useViewAs";

import { MoveImpactAlerts, MoveTargetFields, NEW, ROOT } from "./moveParts";
import { moveConfirmLabel, moveRefusal } from "./moveModel";

/** "collection": within the file's project; "project": into another project. */
export type MoveMode = "collection" | "project";

/** Move a file between a project's collections, or into another project after
 * seeing who would lose it and who would newly see it. */
export default function MoveFileDialog({
  file,
  mode,
  onClose,
  onMoved,
}: {
  file: SecureFile | null;
  mode: MoveMode;
  onClose: () => void;
  /** After the move, before the dialog closes. */
  onMoved?: (file: SecureFile) => void;
}) {
  const dispatch = useDispatch();
  const { readOnly } = useViewAs();
  const [projectChoice, setProjectChoice] = useState("");
  const [collectionChoice, setCollectionChoice] = useState(ROOT);
  const [newName, setNewName] = useState("");
  const [failure, setFailure] = useState<string | null>(null);
  const open = file !== null;

  useEffect(() => {
    setProjectChoice("");
    // Inside a project the dialog starts where the file is: Move waits for a change.
    setCollectionChoice(
      mode === "collection" && file?.collection_id != null
        ? String(file.collection_id)
        : ROOT,
    );
    setNewName("");
    setFailure(null);
  }, [file, mode]);

  const sameProject = mode === "collection";
  const projectId = sameProject
    ? (file?.project_id ?? null)
    : Number(projectChoice) || null;
  const { data: openProjects } = useGetSecureProjectsQuery(
    { client_id: file?.client_id ?? 0, status: "open", page_size: 200 },
    { skip: !open || sameProject || !file?.client_id },
  );
  const { data: collections = [] } = useGetSecureProjectCollectionsQuery(
    projectId ?? 0,
    { skip: !open || !projectId },
  );
  const collectionId =
    collectionChoice === ROOT || collectionChoice === NEW
      ? null
      : Number(collectionChoice);
  // currentData: a check that failed for this target must not leave the last
  // target's people on screen, or the move would add people nobody was shown.
  const {
    currentData: check,
    isFetching: checking,
    error: checkError,
    refetch: checkAgain,
  } = useGetSecureMoveCheckQuery(
    {
      id: file?.id ?? 0,
      project_id: projectId ?? 0,
      collection_id: collectionId,
    },
    { skip: !open || sameProject || !projectId },
  );
  const [move, { isLoading: moving }] = useMoveSecureFileMutation();
  const [createCollection, { isLoading: creating }] =
    useCreateSecureCollectionMutation();
  const busy = moving || creating;

  if (!file) return null;

  const targets = (openProjects?.results ?? []).filter(
    (project) => project.id !== file.project_id,
  );
  // The file's own collection is offered while the list is still loading.
  const choices =
    sameProject &&
    file.collection_id != null &&
    !collections.some((each) => each.id === file.collection_id)
      ? [
          { id: file.collection_id, name: file.collection_name ?? "" },
          ...collections,
        ]
      : collections;
  const losing = check?.losing ?? [];
  const gaining = check?.gaining ?? [];
  const here =
    sameProject &&
    collectionChoice !== NEW &&
    (collectionId ?? null) === (file.collection_id ?? null);
  const ready =
    Boolean(projectId) &&
    !here &&
    !(collectionChoice === NEW && !newName.trim()) &&
    (sameProject || (!checking && !checkError && check !== undefined));

  const submit = async () => {
    if (!projectId) return;
    setFailure(null);
    try {
      let target = collectionId;
      if (collectionChoice === NEW) {
        const made = await createCollection({
          projectId,
          name: newName.trim(),
        }).unwrap();
        target = made.id;
        // A retry after a refused move goes into it instead of making it again.
        setCollectionChoice(String(made.id));
        setNewName("");
      }
      await move({
        id: file.id,
        project_id: projectId,
        collection_id: target,
        add_members: losing.length > 0,
      }).unwrap();
      dispatch(
        pushNotification({ message: "File moved.", severity: "success" }),
      );
      onMoved?.(file);
      onClose();
    } catch (error) {
      setFailure(
        moveRefusal(
          (error as { data?: unknown })?.data,
          "The file could not be moved.",
        ),
      );
      // Who would lose it may have changed since the check: ask again.
      if (!sameProject) void checkAgain();
    }
  };

  const projectName =
    targets.find((project) => project.id === projectId)?.name ?? "the project";

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, overflow: "hidden" } }}
    >
      <DialogContent sx={{ p: 0 }}>
        <FormHeader
          avatar={<DriveFileMoveOutlined fontSize="small" />}
          title={
            sameProject
              ? file.collection_id == null
                ? "Add to Collection"
                : "Move File"
              : file.project_id == null
                ? "Add to Project"
                : "Move to Project"
          }
          onClose={onClose}
          closeDisabled={busy}
        />
        <Box sx={{ p: 3, display: "grid", gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            <b>{file.file_name}</b>
            {check ? ` - from ${check.from}` : ""}
          </Typography>
          <MoveTargetFields
            projects={sameProject ? null : targets}
            projectChoice={projectChoice}
            onProject={(value) => {
              setProjectChoice(value);
              setCollectionChoice(ROOT);
            }}
            projectId={projectId}
            collections={choices}
            collectionChoice={collectionChoice}
            onCollection={setCollectionChoice}
            newName={newName}
            onNewName={setNewName}
            disabled={busy}
          />
          <MoveImpactAlerts
            losing={losing}
            gaining={gaining}
            projectName={projectName}
          />
          {failure ? <Alert severity="error">{failure}</Alert> : null}
        </Box>
      </DialogContent>
      <FormFooter
        mode="edit"
        isFirstStep
        onBack={() => undefined}
        onCancel={onClose}
        cancelDisabled={busy}
      >
        <Button
          variant="contained"
          size="small"
          onClick={submit}
          disabled={!ready || busy || readOnly}
          sx={FOOTER_BUTTON_SX}
        >
          {busy ? "Moving..." : moveConfirmLabel(losing)}
        </Button>
      </FormFooter>
    </Dialog>
  );
}
